import { useState } from "react";
import { MessageCircle, Users, Globe, Target, Star, Bot, Zap, Instagram, Facebook, Sparkles, Headphones, LayoutDashboard, Video, Camera, ChevronDown, PenLine } from "lucide-react";

const ServicesSection = () => {
  const [csExpanded, setCsExpanded] = useState(false);
  const [aiExpanded, setAiExpanded] = useState(false);
  const coreServices = [
    {
      icon: <PenLine className="w-7 h-7" />,
      title: "社群小編經營",
      desc: "專屬小編每日發文、限動、私訊回覆 — IG／FB／LINE@ 一手包辦",
      highlight: true,
      hero: true
    },
    {
      icon: <Instagram className="w-7 h-7" />,
      title: "IG 建置與引流",
      desc: "官方帳號建置 + 網紅合作導流",
      highlight: true
    },
    {
      icon: <Facebook className="w-7 h-7" />,
      title: "FB 建置與引流",
      desc: "粉絲專頁經營 + Meta 精準廣告",
      highlight: true
    },
    {
      icon: <MessageCircle className="w-7 h-7" />,
      title: "LINE@ 官方帳號",
      desc: "選單建置 + 每日成交回報機器人",
      highlight: true
    },
    {
      icon: <Bot className="w-7 h-7" />,
      title: "AI 智能接單系統",
      desc: "開發中，簽約客戶優先使用",
      highlight: true,
      badge: "Beta 開發中"
    },
    {
      icon: <Headphones className="w-7 h-7" />,
      title: "專人客服團隊",
      desc: "最長 10:00–23:00 輪班接單（時段依方案等級）",
      highlight: true
    },
    {
      icon: <Video className="w-7 h-7" />,
      title: "KOL 導流",
      desc: "保障三個月 6-10 位網紅開箱，依物件由顧問與業主約定",
      highlight: true
    },
    {
      icon: <Camera className="w-7 h-7" />,
      title: "素材拍攝與投廣",
      desc: "專業素材拍攝 + Meta/Google 廣告投放",
      highlight: true
    },
    {
      icon: <LayoutDashboard className="w-7 h-7" />,
      title: "PMS 專人收款整理",
      desc: "解決帳務地獄，簽約優先使用自動對帳系統",
      highlight: true
    },
    {
      icon: <Target className="w-7 h-7" />,
      title: "整體行銷漏斗",
      desc: "從曝光到成交的完整轉換路徑，Google SEO ＋ Meta 廣告穩定導流",
      highlight: true
    },
    {
      icon: <Users className="w-7 h-7" />,
      title: "客戶 CRM 管理",
      desc: "IG／FB／LINE@ 業主全程可監督，由我們執行管理與經營，後續優先升級 AI CRM",
      highlight: false
    },
    {
      icon: <Globe className="w-7 h-7" />,
      title: "OTA 平台管理",
      desc: "Booking、Agoda 優化與管理",
      highlight: false
    },
    {
      icon: <Star className="w-7 h-7" />,
      title: "Google 評論經營",
      desc: "客戶平均評分 4.9 以上，圖文並茂提升信賴度",
      highlight: true,
      badge: "4.9+"
    },
    {
      icon: <Sparkles className="w-7 h-7" />,
      title: "客單價住房率提升方案包裝設計",
      desc: "提升平日住房率與整體營收，客單價平均提升 10–25%（多個成功案例）",
      highlight: true,
      hero: true
    }
  ];

  return (
    <section data-testid="services-section" id="services" className="py-14 md:py-32 bg-gradient-to-b from-white to-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-5 md:px-12">
        <div className="text-center mb-10 md:mb-16">
          <div className="inline-flex items-center gap-2 bg-[#FF6B00]/10 text-[#FF6B00] px-4 py-2 rounded-full text-sm font-bold mb-6">
            <Zap className="w-4 h-4" />
            所有方案均包含
          </div>
          <h2 className="text-2xl sm:text-4xl font-bold text-[#0A2540] tracking-tight mb-4">
            一條龍營銷代管服務
          </h2>
          <p className="text-base md:text-lg text-[#475569] max-w-2xl mx-auto px-2">
            <span className="font-bold text-[#0A2540]">民宿主人專心做現場服務，</span>收錢、接單、行銷這些繁瑣事務我們全包辦
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-8 md:mb-12">
          {coreServices.map((service) => (
            <div key={service.title}
              className={`group relative rounded-2xl p-4 md:p-6 shadow-sm hover:shadow-xl transition-all duration-300 border overflow-hidden ${
                service.hero ? 'bg-gradient-to-br from-[#FFF7ED] to-white border-[#FF6B00] ring-2 ring-[#FF6B00]/40 shadow-md' :
                service.highlight ? 'bg-white border-[#FF6B00]/30 ring-1 ring-[#FF6B00]/20' : 'bg-white border-slate-100'
              }`}>
              {service.hero && (
                <div className="absolute top-0 right-0 bg-[#0A2540] text-[#F5C24A] text-xs font-black px-2.5 py-1 rounded-bl-lg flex items-center gap-1">
                  <Star className="w-3 h-3" />主打
                </div>
              )}
              {service.highlight && !service.badge && !service.hero && (
                <div className="absolute top-0 right-0 bg-[#FF6B00] text-white text-xs font-bold px-2 py-1 rounded-bl-lg">
                  重點
                </div>
              )}
              {service.badge && (
                <div className="absolute top-0 right-0 bg-[#0A2540] text-[#FF6B00] text-xs font-bold px-2 py-1 rounded-bl-lg">
                  {service.badge}
                </div>
              )}
              <div className={`w-11 h-11 md:w-14 md:h-14 rounded-xl flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform duration-300 ${
                service.highlight ? 'bg-[#FF6B00] text-white' : 'bg-[#0A2540] text-white'
              }`}>
                {service.icon}
              </div>
              <h3 className={`${service.hero ? "text-base md:text-lg font-black" : "text-sm md:text-base font-bold"} text-[#0A2540] mb-1 md:mb-2`}>{service.title}</h3>
              <p className="hidden md:block text-sm text-[#475569] leading-snug">{service.desc}</p>
            </div>
          ))}
        </div>

        {/* 核心價值強調 - 客服團隊 */}
        <div className="bg-[#0A2540] rounded-3xl p-6 md:p-12 mb-6">
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-center">
            <div>
              <h3 className="text-xl md:text-3xl font-bold text-white mb-3 md:mb-4">
                千萬營收專業客服團隊
              </h3>

              {/* Mobile: simple tagline + expandable details */}
              <p className="md:hidden text-white/85 text-sm leading-relaxed mb-4">
                每日 <span className="text-[#FF6B00] font-bold">最長 10:00–23:00</span> 千萬營收培訓的專業客服輪班接單，<span className="text-white font-bold">大大解放業主時間</span>。
              </p>

              {/* 3 key stats - always visible */}
              <div className="flex flex-wrap gap-2 md:gap-4 mb-4 md:mb-0">
                <div className="bg-white/10 rounded-xl px-3 md:px-4 py-2 md:py-3 flex-1 min-w-[90px]">
                  <div className="text-[#FF6B00] font-black text-lg md:text-2xl">10-23</div>
                  <div className="text-white/70 text-xs md:text-sm">每日服務時段</div>
                </div>
                <div className="bg-white/10 rounded-xl px-3 md:px-4 py-2 md:py-3 flex-1 min-w-[90px]">
                  <div className="text-[#FF6B00] font-black text-lg md:text-2xl">60-95%</div>
                  <div className="text-white/70 text-xs md:text-sm">滿房率/月</div>
                </div>
                <div className="bg-white/10 rounded-xl px-3 md:px-4 py-2 md:py-3 flex-1 min-w-[90px]">
                  <div className="text-[#FF6B00] font-black text-lg md:text-2xl">1次</div>
                  <div className="text-white/70 text-xs md:text-sm">聚焦會議/月</div>
                </div>
              </div>

              <div className="text-white/60 text-xs mt-3 md:mt-4 leading-relaxed">
                ＊客服時段依方案等級：輕量 <span className="text-white/80 font-semibold">10:00–21:00</span>、人客多成長／旗艦 <span className="text-white/80 font-semibold">10:00–23:00</span>；週六、日 10:00–20:00
              </div>

              {/* Mobile: expand button */}
              <button
                type="button"
                data-testid="cs-expand-btn"
                onClick={() => setCsExpanded(!csExpanded)}
                className="md:hidden mt-4 inline-flex items-center gap-1 text-[#FF6B00] text-sm font-bold"
              >
                {csExpanded ? "收起詳細" : "查看服務細節"}
                <ChevronDown className={`w-4 h-4 transition-transform ${csExpanded ? 'rotate-180' : ''}`} />
              </button>

              {/* Full details - desktop always visible, mobile toggled */}
              <div className={`${csExpanded ? 'block' : 'hidden'} md:block mt-4 md:mt-0`}>
                <p className="hidden md:block text-white/80 text-lg mb-4 leading-relaxed">
                  我們的客服團隊<span className="text-[#FF6B00] font-bold">通過接單千萬營收的培訓系統</span>，每日 <span className="text-white font-bold">10:00 - 23:00</span> 輪班接單，<span className="text-[#FF6B00] font-bold">大大解放業主的時間</span>。您不用再為了回覆訊息、報價、收款而煩惱。
                </p>
                <p className="text-white/80 text-sm md:text-base mb-4 leading-relaxed">
                  同時配備<span className="text-[#FF6B00] font-bold">專業客服聚焦小組</span>，<span className="text-white font-bold">每月召開一次聚焦會議</span>，持續優化接單流程與服務品質，確保您的民宿滿房率穩定提升。
                </p>

                {/* PMS 收款整理 */}
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="text-[#FF6B00] font-bold mb-2 text-sm md:text-base">PMS 系統 + 專人收款整理</div>
                  <p className="text-white/70 text-xs md:text-sm leading-relaxed">
                    人客多 PMS 系統配合專人協助訂金與收款整理，<span className="text-white font-semibold">徹底解決帳務地獄</span>。所有接單場域業主可隨時監控，業主提供銀行帳戶存摺封面，合約內將詳細載明相關規範。
                  </p>
                  <p className="text-[#FF6B00] text-xs mt-2 font-semibold">
                    簽約優先使用後續的信用卡金流自動對帳系統
                  </p>
                  <p className="text-white/50 text-xs mt-1">
                    * 經由人客多訂房系統之客戶不在此限
                  </p>
                </div>
              </div>
            </div>
            <div className="hidden md:flex justify-center">
              <div className="relative">
                <div className="w-32 h-32 md:w-40 md:h-40 bg-[#FF6B00] rounded-full flex items-center justify-center">
                  <Headphones className="w-16 h-16 md:w-20 md:h-20 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
                  <Users className="w-8 h-8 text-[#0A2540]" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* AI 智能接單 Beta */}
        <div className="bg-gradient-to-r from-[#0A2540] to-[#1a3a5c] rounded-3xl p-6 md:p-10 border border-white/10">
          <div className="grid md:grid-cols-[1fr_auto] gap-5 md:gap-8 items-center">
            <div>
              <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4 flex-wrap">
                <h3 className="text-lg md:text-2xl font-bold text-white">
                  AI 智能接單系統
                </h3>
                <span className="bg-[#FF6B00] text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse">Beta 開發中</span>
              </div>

              {/* Mobile: short tagline */}
              <p className="md:hidden text-white/85 text-sm leading-relaxed">
                <span className="text-[#FF6B00] font-bold">簽約客戶優先使用</span>。AI 上線前，由千萬營收客服團隊全力支援。
              </p>

              {/* Mobile: expand button */}
              <button
                type="button"
                data-testid="ai-expand-btn"
                onClick={() => setAiExpanded(!aiExpanded)}
                className="md:hidden mt-3 inline-flex items-center gap-1 text-[#FF6B00] text-sm font-bold"
              >
                {aiExpanded ? "收起" : "了解更多"}
                <ChevronDown className={`w-4 h-4 transition-transform ${aiExpanded ? 'rotate-180' : ''}`} />
              </button>

              <div className={`${aiExpanded ? 'block' : 'hidden'} md:block mt-3 md:mt-0`}>
                <p className="hidden md:block text-white/80 text-base mb-3 leading-relaxed">
                  AI 智能接單系統目前正在積極開發中，<span className="text-[#FF6B00] font-bold">簽約客戶將優先使用</span>。在 AI 系統正式上線之前，由我們千萬營收的專業客服團隊全力支援您的接單服務。
                </p>
                <p className="text-white/70 text-xs md:text-sm leading-relaxed">
                  AI 上線後，將實現 <span className="text-white font-bold">24/7 全天候自動報價收款</span>，搭配專人客服形成完整接單體系，讓您的民宿服務永不斷線。
                </p>
              </div>
            </div>
            <div className="flex-shrink-0 hidden md:flex justify-center md:block">
              <div className="w-20 h-20 md:w-28 md:h-28 bg-white/10 rounded-full flex items-center justify-center">
                <Bot className="w-10 h-10 md:w-14 md:h-14 text-[#FF6B00]" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
